import type {
  TvChannelUrlLibraryParams,
  TvChannelUrlSpecialParams,
} from '~/entities/redirect-url'
import type { TvChannelPayment } from '~/features/payment/interfaces'
import type { SignInToWatchItem } from '~/features/signin-to-watch'
import type { AnalyticsQueryParams } from '~/shared/analytix'

import { createEvent, createStore, sample } from 'effector'
import { register } from '~/core/repository'
import { hooks } from '~/core/router'
import { model as tvCategoriesModel } from '~/entities/tv/categories'
import { model as paymentModel } from '~/features/payment'
import * as pinLockFeature from '~/features/pin-lock'
import { model as guestModel } from '~/features/subscriber/guest'
import '~/shared/ui/translations/general-translations'
import { aye, nay } from '~/shared/helpers'
import { model as signInToWatchPopupModel } from '~/widgets/Popups/SignInToWatchPopup'
import { formatSelectedTvChannel } from './helpers'

export const selectActiveChannel = createEvent<
  TvChannelPayment &
    pinLockFeature.IPinLockPopupProps &
    TvChannelUrlLibraryParams &
    TvChannelUrlSpecialParams &
    SignInToWatchItem &
    AnalyticsQueryParams
>()

const defaultSelectChannelState: TvChannelPayment & SignInToWatchItem = {
  id: 0,
  channelNumber: 0,
  categoryId: 0,
  categoryName: undefined,
  name: '',
  logoUrl: '',
  purchaseInfo: null,
  free: true,
  priceSettings: null,
  redirect: '',
}

export const resetSelectedChannel = createEvent()

export const $selectedChannel = createStore<
  TvChannelPayment & SignInToWatchItem
>(defaultSelectChannelState)
  .on(selectActiveChannel, (_, payload) => payload)
  .reset(resetSelectedChannel)

hooks.register(hooks.Left, resetSelectedChannel)

sample({
  clock: selectActiveChannel,
  source: {
    selectedChannel: $selectedChannel,
    isLocked: pinLockFeature.$isLockedFn,
  },
  filter: ({ selectedChannel, isLocked }) => aye(isLocked(selectedChannel)),
  fn: (_, clockData) => clockData,
  target: pinLockFeature.openPinLockPopup,
})

// clear pinlock session storage on close payment popup
sample({
  clock: paymentModel.closePaymentPopup,
  source: {
    selectedChannel: $selectedChannel,
  },
  filter: ({ selectedChannel }, _) => Boolean(selectedChannel.id),
  target: pinLockFeature.resetUnlockId,
})

const selectTvChannel = createEvent<
  TvChannelPayment & TvChannelUrlLibraryParams & TvChannelUrlSpecialParams
>()

sample({
  clock: selectActiveChannel,
  source: {
    selectedChannel: $selectedChannel,
    isLocked: pinLockFeature.$isLockedFn,
    categories: tvCategoriesModel.$tvCategoriesWithChannels,
  },
  filter: ({ selectedChannel, isLocked }) => nay(isLocked(selectedChannel)),
  fn: ({ selectedChannel, categories }) =>
    formatSelectedTvChannel({ selectedChannel, categories }),
  target: selectTvChannel,
})

sample({
  clock: pinLockFeature.$unlockedId,
  source: {
    selectedChannel: $selectedChannel,
    unlockChannelId: pinLockFeature.$unlockedId,
  },
  filter: ({ selectedChannel, unlockChannelId }, value) =>
    aye(value) &&
    selectedChannel.id === unlockChannelId &&
    nay(selectedChannel.isBanner),
  fn: ({ selectedChannel }) => selectedChannel,
  target: selectTvChannel,
})

sample({
  clock: selectTvChannel,
  source: {
    guest: guestModel.$guest,
    selectedChannel: $selectedChannel,
  },
  filter: ({ guest }, { free, purchaseInfo }) =>
    aye(guest) && !free && !purchaseInfo,
  fn: ({ selectedChannel }) => ({
    name: selectedChannel.name,
    redirect: selectedChannel.redirect,
  }),
  target: signInToWatchPopupModel.openSignInToWatchPopup,
})

sample({
  clock: selectTvChannel,
  source: {
    guest: guestModel.$guest,
    selectedChannel: $selectedChannel,
  },
  filter: ({ guest }, { free, purchaseInfo }) =>
    aye(guest) && (aye(free) || aye(purchaseInfo)),
  fn: (_, tvChannel) => tvChannel,
  target: paymentModel.selectTvChannel,
})

sample({
  clock: selectTvChannel,
  source: guestModel.$guest,
  filter: nay,
  fn: (_, tvChannel) => tvChannel,
  target: paymentModel.selectTvChannel,
})

register(selectActiveChannel, 'paymentTvModelWidget/selectActiveChannel')
